import {
  BRAND,
  CITY,
  DEVELOPERS,
  DIRECTOR,
  LIFESTYLES,
  ORG,
  SITE_ORIGIN,
  getNeighborhoodEntity,
} from "@/lib/entityGraph";

const abs = (path: string) => (path.startsWith("http") ? path : `${SITE_ORIGIN}${path.startsWith("/") ? "" : "/"}${path}`);

const clean = <T extends Record<string, any>>(obj: T): T => {
  Object.keys(obj).forEach((k) => {
    const v = (obj as any)[k];
    if (v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0)) delete (obj as any)[k];
  });
  return obj;
};

const orgRef = { "@id": ORG.id };

const imageObjects = (images?: string[] | null, name?: string) =>
  (images || []).filter(Boolean).slice(0, 12).map((url, i) => ({
    "@type": "ImageObject",
    url: abs(url),
    contentUrl: abs(url),
    name: name ? `${name} — foto ${i + 1}` : undefined,
  }));

const breadcrumbs = (items: { name: string; url: string }[]) => ({
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: abs(it.url),
  })),
});

const geoOf = (lat?: number | null, lng?: number | null) =>
  lat && lng ? { "@type": "GeoCoordinates", latitude: lat, longitude: lng } : undefined;

const residenceTypeOf = (propertyType?: string | null) =>
  propertyType === "casa" ? "House" : propertyType === "cobertura" ? "Residence" : "Apartment";

const amenityFeatures = (listing: any) => {
  const all: string[] = [
    ...(listing.features || []),
    ...(listing.condominium_amenities || []),
    ...(listing.unit_amenities || []),
    ...(listing.wellness_features || []),
    ...(listing.appliance_features || []),
    ...(listing.accessibility_features || []),
  ].filter(Boolean);
  return Array.from(new Set(all)).slice(0, 40).map((name) => ({
    "@type": "LocationFeatureSpecification",
    name,
    value: true,
  }));
};

/**
 * Grafo JSON-LD completo de um imóvel:
 * Product + Residence/Apartment/House + Offer + Place + PostalAddress + Geo
 * + ImageObject + VideoObject + 3DModel + BreadcrumbList.
 */
export function buildListingGraph(listing: any, url: string) {
  const nb = getNeighborhoodEntity(listing.neighborhood);
  const lat = listing.latitude ?? nb?.latitude;
  const lng = listing.longitude ?? nb?.longitude;
  const pageUrl = abs(url);
  const name = listing.title;
  const description =
    listing.meta_description ||
    listing.description ||
    `${listing.property_type || "Imóvel"} com ${listing.bedrooms} quartos em ${listing.neighborhood}, Rio de Janeiro.`;
  const images = imageObjects(listing.images, name);
  const primaryImage = listing.image_hero || listing.og_image || (listing.images || [])[0];

  const address = clean({
    "@type": "PostalAddress",
    streetAddress: listing.address || undefined,
    addressLocality: listing.neighborhood || CITY.name,
    addressRegion: "RJ",
    addressCountry: "BR",
  });

  const place = clean({
    "@type": "Place",
    "@id": `${pageUrl}#place`,
    name: `${name} — ${listing.neighborhood}`,
    address,
    geo: geoOf(lat, lng),
    hasMap: lat && lng ? `https://www.google.com/maps/search/?api=1&query=${lat},${lng}` : undefined,
    containedInPlace: { "@type": "City", name: CITY.name, address: { "@type": "PostalAddress", addressRegion: "RJ", addressCountry: "BR" } },
  });

  const offer = clean({
    "@type": "Offer",
    "@id": `${pageUrl}#offer`,
    url: pageUrl,
    price: listing.price ? String(listing.price) : undefined,
    priceCurrency: "BRL",
    availability: listing.is_active === false ? "https://schema.org/SoldOut" : "https://schema.org/InStock",
    itemCondition: "https://schema.org/UsedCondition",
    seller: orgRef,
    businessFunction: "http://purl.org/goodrelations/v1#Sell",
    areaServed: listing.neighborhood || CITY.name,
    priceSpecification: clean({
      "@type": "PriceSpecification",
      price: listing.price ? String(listing.price) : undefined,
      priceCurrency: "BRL",
      valueAddedTaxIncluded: false,
      description: [
        listing.condominium_fee ? `Condomínio: R$ ${listing.condominium_fee}` : null,
        listing.iptu ? `IPTU: R$ ${listing.iptu}` : null,
      ].filter(Boolean).join(" • ") || undefined,
    }),
  });

  const developer = listing.developer || listing.condominium_name
    ? DEVELOPERS.find((d) => (listing.developer || "").toLowerCase().includes(d.name.toLowerCase()))
    : undefined;

  const residence = clean({
    "@type": [residenceTypeOf(listing.property_type), "Residence"],
    "@id": `${pageUrl}#residence`,
    name,
    description,
    url: pageUrl,
    image: images,
    address,
    geo: geoOf(lat, lng),
    hasMap: lat && lng ? `https://www.google.com/maps/search/?api=1&query=${lat},${lng}` : undefined,
    numberOfRooms: listing.bedrooms || undefined,
    numberOfBedrooms: listing.bedrooms || undefined,
    numberOfBathroomsTotal: listing.bathrooms || undefined,
    numberOfFullBathrooms: listing.suites || undefined,
    floorLevel: listing.floor || undefined,
    petsAllowed: true,
    yearBuilt: listing.year_built || undefined,
    floorSize: listing.area_m2
      ? { "@type": "QuantitativeValue", value: listing.area_m2, unitCode: "MTK" }
      : undefined,
    occupancy: listing.bedrooms
      ? { "@type": "QuantitativeValue", value: listing.bedrooms * 2, unitText: "pessoas" }
      : undefined,
    amenityFeature: amenityFeatures(listing),
    containedInPlace: place,
    isPartOf: developer ? { "@type": "ApartmentComplex", name: listing.condominium_name || listing.developer } : undefined,
  });

  const product = clean({
    "@type": "Product",
    "@id": `${pageUrl}#product`,
    name,
    description,
    url: pageUrl,
    sku: listing.listing_code || listing.slug || listing.id,
    productID: listing.listing_code || listing.id,
    category: `Imóveis > ${listing.property_type || "Apartamento"} > ${listing.neighborhood || CITY.name}`,
    image: primaryImage ? abs(primaryImage) : undefined,
    brand: { "@type": "Brand", name: BRAND.name },
    offers: offer,
    isRelatedTo: { "@id": `${pageUrl}#residence` },
    additionalProperty: [
      listing.listing_code && { "@type": "PropertyValue", name: "Código do imóvel", value: listing.listing_code },
      listing.property_type && { "@type": "PropertyValue", name: "Tipo", value: listing.property_type },
      listing.garage_spots != null && { "@type": "PropertyValue", name: "Vagas", value: listing.garage_spots },
      listing.suites != null && { "@type": "PropertyValue", name: "Suítes", value: listing.suites },
      listing.area_m2 && { "@type": "PropertyValue", name: "Área útil (m²)", value: listing.area_m2 },
      listing.condominium_fee && { "@type": "PropertyValue", name: "Condomínio (R$)", value: listing.condominium_fee },
      listing.iptu && { "@type": "PropertyValue", name: "IPTU (R$)", value: listing.iptu },
      listing.is_furnished != null && { "@type": "PropertyValue", name: "Mobiliado", value: listing.is_furnished ? "Sim" : "Não" },
      listing.near_metro && { "@type": "PropertyValue", name: "Próximo ao metrô", value: "Sim" },
      listing.developer && { "@type": "PropertyValue", name: "Construtora", value: listing.developer },
    ].filter(Boolean),
  });

  const nodes: any[] = [product, residence];

  if (listing.video_url || listing.drone_url) {
    nodes.push(clean({
      "@type": "VideoObject",
      "@id": `${pageUrl}#video`,
      name: `Vídeo — ${name}`,
      description,
      contentUrl: listing.video_url || listing.drone_url,
      embedUrl: listing.video_url || listing.drone_url,
      thumbnailUrl: primaryImage ? abs(primaryImage) : undefined,
      uploadDate: listing.created_at || undefined,
      publisher: orgRef,
    }));
  }

  if (listing.virtual_tour_url) {
    nodes.push({
      "@type": "3DModel",
      "@id": `${pageUrl}#tour`,
      name: `Tour virtual — ${name}`,
      contentUrl: listing.virtual_tour_url,
      encodingFormat: "text/html",
      about: { "@id": `${pageUrl}#residence` },
    });
  }

  if (listing.floorplan_url) {
    nodes.push({
      "@type": "ImageObject",
      "@id": `${pageUrl}#floorplan`,
      name: `Planta — ${name}`,
      url: abs(listing.floorplan_url),
      contentUrl: abs(listing.floorplan_url),
      about: { "@id": `${pageUrl}#residence` },
    });
  }

  const faqs = Array.isArray(listing.faqs) ? listing.faqs.filter((f: any) => f?.question && f?.answer) : [];
  if (faqs.length) {
    nodes.push({
      "@type": "FAQPage",
      "@id": `${pageUrl}#faq`,
      mainEntity: faqs.map((f: any) => ({
        "@type": "Question",
        name: f.question,
        acceptedAnswer: { "@type": "Answer", text: f.answer },
      })),
    });
  }

  nodes.push(
    breadcrumbs([
      { name: "Home", url: "/" },
      { name: listing.neighborhood || CITY.name, url: `/${(getNeighborhoodEntity(listing.neighborhood)?.slug) || ""}` },
      { name, url },
    ]),
  );

  return { "@context": "https://schema.org", "@graph": nodes };
}

/**
 * Grafo JSON-LD de um lançamento/empreendimento:
 * ApartmentComplex + Residence + OfferCatalog + Place + GeoCoordinates
 * + VideoObject + FAQPage + Review/AggregateRating (quando houver).
 */
export function buildLaunchGraph(launch: any, url: string) {
  const nb = getNeighborhoodEntity(launch.neighborhood);
  const lat = launch.latitude ?? nb?.latitude;
  const lng = launch.longitude ?? nb?.longitude;
  const pageUrl = abs(url);
  const name = launch.name || launch.title;
  const description =
    launch.seo_description || launch.descricao_curta || launch.description ||
    `Lançamento ${name} em ${launch.neighborhood}, Rio de Janeiro.`;
  const images = imageObjects([launch.hero_image_url, ...(launch.images || []), ...(launch.galeria || []).map((item: any) => item.imagem_url)].filter(Boolean), name);

  const address = clean({
    "@type": "PostalAddress",
    streetAddress: launch.address || undefined,
    addressLocality: launch.neighborhood || CITY.name,
    addressRegion: "RJ",
    addressCountry: "BR",
  });

  const place = clean({
    "@type": "Place",
    "@id": `${pageUrl}#place`,
    name: `${name} — ${launch.neighborhood}`,
    address,
    geo: geoOf(lat, lng),
    hasMap: lat && lng ? `https://www.google.com/maps/search/?api=1&query=${lat},${lng}` : undefined,
  });

  const typologies: string[] = Array.isArray(launch.typologies) ? launch.typologies : [];

  const complex = clean({
    "@type": ["ApartmentComplex", "Residence"],
    "@id": `${pageUrl}#development`,
    name,
    description,
    url: pageUrl,
    image: images,
    address,
    geo: geoOf(lat, lng),
    hasMap: lat && lng ? `https://www.google.com/maps/search/?api=1&query=${lat},${lng}` : undefined,
    containedInPlace: place,
    numberOfAvailableAccommodationUnits: typologies.length || undefined,
    floorSize:
      launch.area_min || launch.area_max
        ? { "@type": "QuantitativeValue", minValue: launch.area_min || undefined, maxValue: launch.area_max || undefined, unitCode: "MTK" }
        : undefined,
    petsAllowed: true,
    provider: (launch.construtora?.nome || launch.developer) ? { "@type": "Organization", name: launch.construtora?.nome || launch.developer } : orgRef,
    amenityFeature: amenityFeatures(launch),
    additionalProperty: [
      launch.delivery_date && { "@type": "PropertyValue", name: "Entrega", value: launch.delivery_date },
      launch.down_payment && { "@type": "PropertyValue", name: "Entrada", value: launch.down_payment },
      launch.developer && { "@type": "PropertyValue", name: "Construtora", value: launch.developer },
      typologies.length && { "@type": "PropertyValue", name: "Tipologias", value: typologies.join(", ") },
    ].filter(Boolean),
  });

  const units = Array.isArray(launch.unidades) ? launch.unidades.filter((unit: any) => unit.status === "disponivel") : [];
  const prices = units.map((unit: any) => Number(unit.valor_tabela)).filter(Number.isFinite);
  const catalog = clean({
    "@type": "OfferCatalog",
    "@id": `${pageUrl}#catalog`,
    name: `Unidades — ${name}`,
    provider: orgRef,
    itemListElement: (units.length ? units : (typologies.length ? typologies : [name])).map((item: any) =>
      clean({
        "@type": "Offer",
        name: typeof item === "string" ? item : `Unidade ${item.unidade} — ${item.tipologia}`,
        priceCurrency: "BRL",
        price: String(typeof item === "string" ? launch.preco_a_partir || launch.price_min || "" : item.valor_tabela),
        priceSpecification: clean({
          "@type": "PriceSpecification",
          priceCurrency: "BRL",
          minPrice: prices.length ? Math.min(...prices) : launch.preco_a_partir || launch.price_min || undefined,
          maxPrice: prices.length ? Math.max(...prices) : launch.price_max || undefined,
        }),
        availability: launch.status_obra === "pronto_para_morar" ? "https://schema.org/InStock" : "https://schema.org/PreOrder",
        url: pageUrl,
        seller: orgRef,
      }),
    ),
  });

  const nodes: any[] = [complex, catalog];

  if (launch.video_url) {
    nodes.push(clean({
      "@type": "VideoObject",
      "@id": `${pageUrl}#video`,
      name: `Vídeo — ${name}`,
      description,
      contentUrl: launch.video_url,
      embedUrl: launch.video_url,
      thumbnailUrl: (launch.images || [])[0] ? abs((launch.images || [])[0]) : undefined,
      uploadDate: launch.created_at || undefined,
      publisher: orgRef,
    }));
  }

  const faqs = Array.isArray(launch.faqs) ? launch.faqs.filter((f: any) => f?.question && f?.answer) : [];
  nodes.push({
    "@type": "FAQPage",
    "@id": `${pageUrl}#faq`,
    mainEntity: (faqs.length
      ? faqs
      : [
          {
            question: `Onde fica o ${name}?`,
            answer: `O ${name} está localizado em ${launch.neighborhood || CITY.name}, Rio de Janeiro${launch.address ? ` (${launch.address})` : ""}.`,
          },
          {
            question: `Quais as tipologias do ${name}?`,
            answer: typologies.length
              ? `As tipologias disponíveis são: ${typologies.join(", ")}.`
              : `Fale com a ${ORG.name} pelo WhatsApp para receber as plantas e tipologias disponíveis.`,
          },
          {
            question: `Quando é a entrega do ${name}?`,
            answer: launch.delivery_date
              ? `A entrega prevista é ${launch.delivery_date}.`
              : `Consulte a ${ORG.name} para a previsão de entrega atualizada.`,
          },
        ]
    ).map((f: any) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: { "@type": "Answer", text: f.answer },
    })),
  });

  if (launch.rating_value && launch.rating_count) {
    nodes.push({
      "@type": "AggregateRating",
      itemReviewed: { "@id": `${pageUrl}#development` },
      ratingValue: launch.rating_value,
      reviewCount: launch.rating_count,
    });
  }
  if (Array.isArray(launch.reviews)) {
    launch.reviews
      .filter((r: any) => r?.author && r?.body)
      .forEach((r: any) =>
        nodes.push(clean({
          "@type": "Review",
          itemReviewed: { "@id": `${pageUrl}#development` },
          author: { "@type": "Person", name: r.author },
          reviewBody: r.body,
          reviewRating: r.rating ? { "@type": "Rating", ratingValue: r.rating, bestRating: 5 } : undefined,
        })),
      );
  }

  nodes.push(
    breadcrumbs([
      { name: "Home", url: "/" },
      { name: "Lançamentos", url: "/lancamentos" },
      { name, url },
    ]),
  );

  return { "@context": "https://schema.org", "@graph": nodes };
}

/** Grafo de entidades (usado por páginas de bairro para reforçar autoridade temática). */
export function buildNeighborhoodGraph(neighborhoodName: string, url: string) {
  const nb = getNeighborhoodEntity(neighborhoodName);
  const pageUrl = abs(url);
  return {
    "@context": "https://schema.org",
    "@graph": [
      clean({
        "@type": "Place",
        "@id": `${pageUrl}#place`,
        name: nb?.name || neighborhoodName,
        alternateName: nb?.alternateNames,
        description: nb?.description,
        url: pageUrl,
        geo: geoOf(nb?.latitude, nb?.longitude),
        containedInPlace: { "@type": "City", name: CITY.name },
      }),
      {
        "@type": "RealEstateAgent",
        "@id": `${SITE_ORIGIN}/#realestateagent`,
        name: ORG.name,
        areaServed: nb?.name || neighborhoodName,
        employee: { "@type": "Person", name: DIRECTOR.name, jobTitle: DIRECTOR.jobTitle, identifier: DIRECTOR.creci },
        knowsAbout: LIFESTYLES.map((l) => l.name),
      },
      breadcrumbs([
        { name: "Home", url: "/" },
        { name: nb?.name || neighborhoodName, url },
      ]),
    ],
  };
}
